import { useState } from 'react';
import { AlertCircle, Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { log } from '../lib/logger';

interface ErrorMessageProps {
  message: string;
  technicalError?: string;
  className?: string;
  onDismiss?: () => void;
}

export function ErrorMessage({ message, technicalError, className = '', onDismiss }: ErrorMessageProps) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const handleCopy = async () => {
    const textToCopy = technicalError
      ? `Error: ${message}\n\nTechnical Details:\n${technicalError}`
      : message;

    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      log.error('Failed to copy:', err);
    }
  };

  return (
    <div className={`bg-red-50 ring-1 ring-red-100 rounded-2xl shadow-sm p-5 ${className}`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 p-2 bg-red-100 rounded-full">
          <AlertCircle className="w-5 h-5 text-red-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-red-800 font-semibold leading-tight">{message}</p>
          <p className="text-red-600/80 text-sm mt-1">{t('error.tryRefresh')}</p>

          {technicalError && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700 transition-colors"
              >
                {showDetails ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
                {t('error.technicalDetails')}
              </button>

              {showDetails && (
                <div className="mt-2 p-3 bg-red-100/70 rounded-xl ring-1 ring-red-100">
                  <div className="flex items-start justify-between gap-2">
                    <code className="text-xs text-red-800 break-all whitespace-pre-wrap font-mono">
                      {technicalError}
                    </code>
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="flex-shrink-0 p-1.5 text-red-600 hover:text-red-700 hover:bg-red-200 rounded transition-colors"
                      title={t('error.copyDetails')}
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {copied && (
                    <p className="text-xs text-green-600 mt-1">{t('error.copied')}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="text-red-400 hover:text-red-600 transition-colors"
          >
            <span className="sr-only">Dismiss</span>
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
